import React from 'react';

import BASKET_UPDATE_TYPES from '@routes/Basket/components/BasketUpdateTypes';

import { EyebrowTitle, Title4 } from '@common/components/Typography';
import { useTranslations } from '@lang';
import { ContentPage } from 'shared-types/Common';
import { Room } from '../Room';
import { SpaItem } from '../SpaItem';
import { Table } from '../Table';

import { useLabels } from '@common/features/Menu/useLabels';
import { PackageItemCategory } from '../styled';

import { BasketItemPriceWrapper, BasketItemRemoveButton } from '../../BasketItem/BasketItem.styles';
import { DiscountPrice } from '../../Price/DiscountPrice';
import { GroupedBasketItemTypes } from '../ItemListingRows';
import { PackageItemsWrapper, PackageTitleRow } from '../styled';

export type PackageProps = {
  onChange: (
    type: BASKET_UPDATE_TYPES.QUANTITY | BASKET_UPDATE_TYPES.REMOVE,
    itemId: number | string,
    quantity?: number
  ) => void;
  items: GroupedBasketItemTypes;
  isEditable: boolean;
  tablesPage?: ContentPage;
  tablesDisableChildBookings?: boolean;
};

export const Package: React.FC<PackageProps> = ({
  onChange,
  items,
  isEditable,
  tablesPage,
  tablesDisableChildBookings,
}) => {
  const {
    tables,
    rooms,
    'spa-treatments': spaTreatments,
    'spa-facilities': spaFacilities,
    'spa-packages': spaPackages,
    'spa-classes': spaClasses,
  } = items;
  const labels = useLabels();
  const { t } = useTranslations();

  const spaItems = [
    ...(spaTreatments ?? []),
    ...(spaFacilities ?? []),
    ...(spaPackages ?? []),
    ...(spaClasses ?? []),
  ];

  const room = items?.rooms?.[0]?.item?.bookable;

  function handlePackageRemoval() {
    const roomItem = items?.rooms?.[0]?.item;
    if (roomItem) {
      onChange(BASKET_UPDATE_TYPES.REMOVE, roomItem.id);
    }
  }

  const roomExtrasTotal =
    room?.roomExtras?.reduce((acc, extra) => acc + (extra.price?.amount || 0), 0) || 0;
  const spaExtrasTotal =
    spaPackages?.reduce((acc, spaPackage) => {
      spaPackage.item?.selectionGroups?.forEach((sg) => {
        if (sg.isExtras) {
          sg.selections?.forEach((s) => {
            acc += s.price?.amount;
          });
        }
      });
      return acc;
    }, 0) || 0;
  const extrasTotalPrice = roomExtrasTotal + spaExtrasTotal;

  const packageTotalPrice = room?.price && {
    ...room.price,
    amount: (room.price.amount || 0) + extrasTotalPrice,
  };

  const packageDiscountPrice = room?.discount && {
    ...room.discount,
    discountedPrice: room.discount?.discountedPrice && {
      ...room.discount?.discountedPrice,
      amount: (room.discount.discountedPrice?.amount || 0) + extrasTotalPrice,
    },
  };
  return (
    <>
      <PackageTitleRow>
        <Title4>{items?.rooms?.[0].item?.bookable.rate || ''}</Title4>
        <div>
          <BasketItemPriceWrapper>
            <DiscountPrice price={packageTotalPrice} discount={packageDiscountPrice} />
          </BasketItemPriceWrapper>
          <BasketItemRemoveButton href="#" onClick={handlePackageRemoval}>
            {t('common.remove')}
          </BasketItemRemoveButton>
        </div>
      </PackageTitleRow>
      <PackageItemsWrapper>
        {rooms && (
          <PackageItemCategory isLastItem={false}>
            <EyebrowTitle>{labels?.rooms || t('basket.rooms')}</EyebrowTitle>
            {rooms.map(({ item }) => (
              <Room
                key={item.id}
                item={item}
                onChange={onChange}
                isEditable={isEditable}
                hidePrice
              />
            ))}
          </PackageItemCategory>
        )}
        {!!spaItems.length && (
          <PackageItemCategory isLastItem={!tables?.length}>
            <EyebrowTitle>{labels?.spa || t('basket.spa')}</EyebrowTitle>
            {spaItems?.map(({ item }) => {
              return (
                <SpaItem
                  key={item.id}
                  item={item}
                  onChange={onChange}
                  isEditable={isEditable}
                  hideArrivalTimes
                  hidePrice
                  isPackage
                />
              );
            })}
          </PackageItemCategory>
        )}
        {tables && (
          <PackageItemCategory isLastItem>
            <EyebrowTitle>{t('basket.diningReservation')}</EyebrowTitle>
            {tables.map(({ item }) => (
              <Table
                key={item.id}
                item={item}
                onChange={onChange}
                isEditable={isEditable}
                thumbnail={tablesPage?.heroBackgroundImage?.url || ''}
                tablesDisableChildBookings={!!tablesDisableChildBookings}
                hidePrice
                hideGuestDetails
              />
            ))}
          </PackageItemCategory>
        )}
      </PackageItemsWrapper>
    </>
  );
};
